import { CheckCircle, Users, FileText, Clock } from 'lucide-react';

interface Step {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  status: 'completed' | 'current' | 'upcoming';
}

interface SubmissionProgressProps {
  ideaStatus?: 'submitted_initial' | 'under_mentor_review' | 'submitted_final' | 'selected' | 'rejected';
}

/**
 * Map backend idea status to progress stages
 * Progress stages:
 * 1. Initial Submission → when status is submitted_initial
 * 2. Mentor Review Window → when status is under_mentor_review
 * 3. Final Idea Submission → when status is submitted_final
 * 4. Jury Evaluation → when status is selected or rejected
 */
function getProgressSteps(ideaStatus?: string): Step[] {
  // Determine which steps are completed/current/upcoming based on status
  let steps: Step[];

  if (ideaStatus === 'submitted_initial') {
    steps = [
      {
        id: '1',
        title: 'Initial Submission',
        icon: <CheckCircle className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '2',
        title: 'Mentor Review',
        subtitle: 'Window',
        icon: <Users className="w-6 h-6" />,
        status: 'upcoming',
      },
      {
        id: '3',
        title: 'Final Idea',
        subtitle: 'Submission',
        icon: <FileText className="w-6 h-6" />,
        status: 'upcoming',
      },
      {
        id: '4',
        title: 'Jury Evaluation',
        icon: <Clock className="w-6 h-6" />,
        status: 'upcoming',
      },
    ];
  } else if (ideaStatus === 'under_mentor_review') {
    steps = [
      {
        id: '1',
        title: 'Initial Submission',
        icon: <CheckCircle className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '2',
        title: 'Mentor Review',
        subtitle: 'Window',
        icon: <Users className="w-6 h-6" />,
        status: 'current',
      },
      {
        id: '3',
        title: 'Final Idea',
        subtitle: 'Submission',
        icon: <FileText className="w-6 h-6" />,
        status: 'upcoming',
      },
      {
        id: '4',
        title: 'Jury Evaluation',
        icon: <Clock className="w-6 h-6" />,
        status: 'upcoming',
      },
    ];
  } else if (ideaStatus === 'submitted_final') {
    steps = [
      {
        id: '1',
        title: 'Initial Submission',
        icon: <CheckCircle className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '2',
        title: 'Mentor Review',
        subtitle: 'Window',
        icon: <Users className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '3',
        title: 'Final Idea',
        subtitle: 'Submission',
        icon: <FileText className="w-6 h-6" />,
        status: 'current',
      },
      {
        id: '4',
        title: 'Jury Evaluation',
        icon: <Clock className="w-6 h-6" />,
        status: 'upcoming',
      },
    ];
  } else if (ideaStatus === 'selected' || ideaStatus === 'rejected') {
    steps = [
      {
        id: '1',
        title: 'Initial Submission',
        icon: <CheckCircle className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '2',
        title: 'Mentor Review',
        subtitle: 'Window',
        icon: <Users className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '3',
        title: 'Final Idea',
        subtitle: 'Submission',
        icon: <FileText className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '4',
        title: 'Jury Evaluation',
        icon: <Clock className="w-6 h-6" />,
        status: 'completed',
      },
    ];
  } else {
    // Default fallback for unknown status
    steps = [
      {
        id: '1',
        title: 'Initial Submission',
        icon: <CheckCircle className="w-6 h-6" />,
        status: 'completed',
      },
      {
        id: '2',
        title: 'Mentor Review',
        subtitle: 'Window',
        icon: <Users className="w-6 h-6" />,
        status: 'upcoming',
      },
      {
        id: '3',
        title: 'Final Idea',
        subtitle: 'Submission',
        icon: <FileText className="w-6 h-6" />,
        status: 'upcoming',
      },
      {
        id: '4',
        title: 'Jury Evaluation',
        icon: <Clock className="w-6 h-6" />,
        status: 'upcoming',
      },
    ];
  }

  return steps;
}

export function SubmissionProgress({ ideaStatus }: SubmissionProgressProps) {
  const steps = getProgressSteps(ideaStatus);

  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm">
      <h2 className="text-2xl mb-8">Submission Progress</h2>
      
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute top-8 left-0 right-0 h-0.5 bg-gray-300" 
             style={{ left: '2rem', right: '2rem' }} />
        
        {/* Steps */}
        <div className="relative flex justify-between items-start">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center" style={{ flex: '1' }}>
              {/* Icon Circle */}
              <div
                className={`
                  relative z-10 w-16 h-16 rounded-full flex items-center justify-center
                  ${step.status === 'completed' 
                    ? 'bg-blue-600 text-white' 
                    : step.status === 'current'
                    ? 'bg-white border-4 border-blue-600 text-blue-600'
                    : 'bg-white border-2 border-gray-300 text-gray-400'
                  }
                `}
              >
                {step.icon}
              </div>
              
              {/* Label */}
              <div className="mt-4 text-center max-w-[140px]">
                <div className={`
                  text-sm
                  ${step.status === 'current' ? 'text-blue-600' : 'text-gray-900'}
                `}>
                  {step.title}
                </div>
                {step.subtitle && (
                  <div className={`
                    text-sm
                    ${step.status === 'current' ? 'text-blue-600' : 'text-gray-900'}
                  `}>
                    {step.subtitle}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
